import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ResponsiveContainer,
    ComposedChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Cell,
} from 'recharts';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber, formatCurrency } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

interface CostItem {
    name: string;
    base: number;
    value: number;
    fill: string;
}

export const RefundTrueCostChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const chartData = useMemo((): CostItem[] => {
        if (!finance?.metrics) return [];

        const returns = finance.metrics.returns ?? 0;
        const grossSales = finance.metrics.grossSales ?? 0;
        const adSpend = finance.metrics.adSpend ?? 0;

        if (returns === 0) return [];

        // Estimate wasted ad spend: proportion of returns relative to gross sales
        const wastedAdSpend = grossSales > 0 ? (returns / grossSales) * adSpend : 0;
        const totalTrueCost = returns + wastedAdSpend;

        const items: CostItem[] = [
            {
                name: t('charts.refundAmount', 'Refund Amount'),
                base: 0,
                value: returns,
                fill: CHART_COLORS.expense,
            },
            {
                name: t('charts.wastedAdSpend', 'Wasted Ad Spend'),
                base: returns,
                value: wastedAdSpend,
                fill: CHART_COLORS.accent,
            },
            {
                name: t('charts.totalTrueCost', 'Total True Cost'),
                base: 0,
                value: totalTrueCost,
                fill: '#991B1B', // Red-800 for dark red
            },
        ];

        return items;
    }, [finance?.metrics, t]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    const totalCost = useMemo(() => {
        const last = chartData.find((d) => d.name === t('charts.totalTrueCost', 'Total True Cost'));
        return last?.value ?? 0;
    }, [chartData, t]);

    useSetChartMetadata({
        isEmpty,
        summary: !isEmpty ? (
            <span style={{ color: CHART_COLORS.expense }}>
                {formatCompactNumber(totalCost, currencySymbol)}
            </span>
        ) : undefined,
    });

    if (isEmpty) return null;

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData} margin={CHART_STYLES.margin.withAxis}>
                        <CartesianGrid
                            vertical={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            dataKey="name"
                            {...CHART_STYLES.axis}
                            interval={0}
                            angle={-20}
                            textAnchor="end"
                            height={60}
                        />
                        <YAxis
                            tickFormatter={(v: number) => formatCompactNumber(v, currencySymbol)}
                            {...CHART_STYLES.axis}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            itemStyle={CHART_STYLES.tooltip.itemStyle}
                            formatter={(value: number) => [formatCurrency(value, currencySymbol)]}
                            labelFormatter={(label: string) => label}
                        />
                        {/* Invisible base bar for waterfall offset */}
                        <Bar dataKey="base" stackId="truecost" fill="transparent" />
                        <Bar
                            dataKey="value"
                            stackId="truecost"
                            radius={CHART_STYLES.bar.radius}
                            maxBarSize={CHART_STYLES.bar.maxBarSize}
                        >
                            {chartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                        </Bar>
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
